import React from "react";
import Icon from "../../components/AppIcon";
import AppLogo from "../../components/ui/AppLogo";
import { cn } from "../../utils/cn";

/**
 * Read-only replica of NavigationSidebar + main column.
 * Labels/icons/structure mirror production for tour fidelity.
 */
const FakeAppChrome = ({
  activeNavId = "home",
  documentsOpen: documentsOpenProp,
  crmOpen: crmOpenProp,
  children,
  className,
}) => {
  const documentsOpen =
    documentsOpenProp ??
    [
      "library",
      "generator",
      "write-contract",
      "uploaded",
      "create-template",
    ].includes(activeNavId);
  const crmOpen = crmOpenProp ?? activeNavId === "crm";

  const linkClassName = (active) =>
    cn(
      "relative flex items-center px-3 py-2.5 rounded-lg",
      active
        ? "bg-[var(--color-sidebar-active)] text-primary font-semibold shadow-soft-sm before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[3px] before:rounded-full before:bg-[var(--color-sidebar-active-border)]"
        : "text-muted-foreground"
    );

  const subLinkClassName = (active) =>
    cn(
      "flex items-center pl-10 pr-3 py-2 rounded-lg text-sm",
      active
        ? "bg-primary/10 text-primary font-semibold"
        : "text-muted-foreground"
    );

  return (
    <div
      className={cn(
        "flex min-h-full w-full flex-1 overflow-hidden rounded-lg border border-border bg-background shadow-elevation-sm",
        className
      )}
    >
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border bg-card/95 backdrop-blur-xl">
        <div className="flex h-20 items-center justify-center border-b border-border px-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl">
            <AppLogo size="sm" variant="mark" />
          </div>
          <span className="ml-3 text-lg font-bold tracking-tight text-foreground">
            neo-modelos
          </span>
        </div>

        <nav className="flex flex-col space-y-1 overflow-y-auto p-3" aria-hidden>
          <div className={linkClassName(activeNavId === "home")}>
            <Icon name="Home" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium">Home</span>
          </div>

          <div className="space-y-1">
            <div
              className={cn(
                linkClassName(
                  documentsOpen &&
                    [
                      "library",
                      "generator",
                      "write-contract",
                      "uploaded",
                      "create-template",
                    ].includes(activeNavId)
                ),
                "w-full"
              )}
            >
              <Icon name="Files" size={20} className="flex-shrink-0" />
              <span className="ml-3 flex-1 text-left text-sm font-medium">
                Documentos
              </span>
              <Icon
                name={documentsOpen ? "ChevronUp" : "ChevronDown"}
                size={16}
                className="flex-shrink-0 opacity-70"
              />
            </div>
            {documentsOpen ? (
              <div className="space-y-0.5">
                <div className={subLinkClassName(activeNavId === "write-contract")}>
                  <Icon name="PenLine" size={16} className="mr-2.5 flex-shrink-0" />
                  Criar Documento
                </div>
                <div
                  className={subLinkClassName(
                    activeNavId === "library" || activeNavId === "create-template"
                  )}
                >
                  <Icon name="Library" size={16} className="mr-2.5 flex-shrink-0" />
                  Biblioteca de Modelos
                </div>
                <div className={subLinkClassName(activeNavId === "generator")}>
                  <Icon name="FileText" size={16} className="mr-2.5 flex-shrink-0" />
                  Gerar Documento
                </div>
                <div className={subLinkClassName(activeNavId === "uploaded")}>
                  <Icon name="Upload" size={16} className="mr-2.5 flex-shrink-0" />
                  Enviar para Assinatura
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-1">
            <div className={cn(linkClassName(crmOpen), "w-full")}>
              <Icon name="ContactRound" size={20} className="flex-shrink-0" />
              <span className="ml-3 flex-1 text-left text-sm font-medium">
                CRM Neo Documentos
              </span>
              <Icon
                name={crmOpen ? "ChevronUp" : "ChevronDown"}
                size={16}
                className="flex-shrink-0 opacity-70"
              />
            </div>
            {crmOpen ? (
              <div className="space-y-0.5">
                <div className={subLinkClassName(activeNavId === "crm")}>
                  <Icon name="Kanban" size={16} className="mr-2.5 flex-shrink-0" />
                  Leads
                </div>
                <div className={subLinkClassName(false)}>
                  <Icon
                    name="MessageSquareText"
                    size={16}
                    className="mr-2.5 flex-shrink-0"
                  />
                  Respostas Padrão
                </div>
              </div>
            ) : null}
          </div>

          <div className={linkClassName(false)}>
            <Icon name="Users" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium">Clientes</span>
          </div>
          <div className={linkClassName(false)}>
            <Icon name="ShoppingCart" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium">Comprar Licença</span>
          </div>
          <div className={linkClassName(false)}>
            <Icon name="BadgeCheck" size={20} className="flex-shrink-0" />
            <span className="ml-3 text-sm font-medium">Minha Licença</span>
          </div>
        </nav>
      </aside>

      <div className="min-h-full min-w-0 flex-1 app-shell-bg">{children}</div>
    </div>
  );
};

export default FakeAppChrome;
